import React, { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { toast, Toaster } from "sonner";
import { fmtMoney, fmtPct } from "../lib/exports";
import { Plus, Edit, Trash2 } from "lucide-react";

export default function Productos() {
  const { user } = useAuth();
  const puedeEditar = canEdit(user, "productos");
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/productos", { params: { incluir_inactivos: true } });
      setProductos(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  const eliminar = async (id) => {
    if (!window.confirm("¿Desactivar producto?")) return;
    try { await api.delete(`/productos/${id}`); toast.success("Producto desactivado"); cargar(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Catálogo</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Productos & Costos</h1>
          <p className="text-sm text-slate-500 mt-1">Precio, costo y margen por producto.</p>
        </div>
        {puedeEditar && (
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild>
              <Button data-testid="prod-nuevo" className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="w-4 h-4 mr-2" />Nuevo Producto</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuevo Producto</DialogTitle></DialogHeader>
              <ProductForm onSaved={() => { setShowNew(false); cargar(); }} onCancel={() => setShowNew(false)} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr>
                <Th>Producto</Th><Th right>Precio</Th><Th right>Costo</Th><Th right>Utilidad</Th><Th right>Margen</Th>
                <Th right>Pollos Eq.</Th><Th>Semáforo</Th><Th>Estado</Th>
                {puedeEditar && <Th>Acciones</Th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={puedeEditar ? 9 : 8} className="px-4 py-8 text-center text-slate-500">Cargando…</td></tr>}
              {productos.map((p) => {
                const utilidad = (p.precio_venta || 0) - (p.costo_unitario || 0);
                const margen = p.precio_venta > 0 ? (utilidad / p.precio_venta) * 100 : 0;
                const semaforo = margen >= 55 ? "verde" : margen >= 45 ? "amarillo" : "rojo";
                return (
                  <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50 ${!p.activo ? "opacity-50" : ""}`} data-testid={`prod-row-${p.id}`}>
                    <Td className="font-medium">{p.nombre}</Td>
                    <Td right mono>{fmtMoney(p.precio_venta)}</Td>
                    <Td right mono>{fmtMoney(p.costo_unitario)}</Td>
                    <Td right mono className={utilidad >= 0 ? "text-emerald-600" : "text-red-600"}>{fmtMoney(utilidad)}</Td>
                    <Td right mono className={semaforo === "verde" ? "text-emerald-600" : semaforo === "amarillo" ? "text-amber-600" : "text-red-600"}>
                      {p.precio_venta > 0 ? fmtPct(margen) : "—"}
                    </Td>
                    <Td right mono>{p.pollos_equivalentes}</Td>
                    <Td>
                      {p.precio_venta > 0 && (
                        <Badge className={
                          semaforo === "verde" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" :
                          semaforo === "amarillo" ? "bg-amber-100 text-amber-800 hover:bg-amber-100" :
                          "bg-red-100 text-red-800 hover:bg-red-100"
                        }>
                          {semaforo === "verde" ? "Óptimo" : semaforo === "amarillo" ? "Revisar" : "Subir precio"}
                        </Badge>
                      )}
                    </Td>
                    <Td><Badge variant={p.activo ? "default" : "secondary"}>{p.activo ? "Activo" : "Inactivo"}</Badge></Td>
                    {puedeEditar && (
                      <Td>
                        <Button variant="ghost" size="icon" onClick={() => setEditing(p)} data-testid={`prod-edit-${p.id}`}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => eliminar(p.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </Td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar Producto</DialogTitle></DialogHeader>
          {editing && <ProductForm producto={editing} onSaved={() => { setEditing(null); cargar(); }} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProductForm({ producto, onSaved, onCancel }) {
  const [form, setForm] = useState({
    nombre: producto?.nombre || "",
    precio_venta: producto?.precio_venta ?? 0,
    costo_unitario: producto?.costo_unitario ?? 0,
    pollos_equivalentes: producto?.pollos_equivalentes ?? 0,
    activo: producto?.activo ?? true,
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const guardar = async () => {
    setBusy(true);
    try {
      const payload = {
        nombre: form.nombre,
        precio_venta: parseFloat(form.precio_venta) || 0,
        costo_unitario: parseFloat(form.costo_unitario) || 0,
        pollos_equivalentes: parseFloat(form.pollos_equivalentes) || 0,
        activo: form.activo,
      };
      if (producto) await api.put(`/productos/${producto.id}`, payload);
      else await api.post("/productos", payload);
      toast.success("Guardado");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label>Nombre</Label>
        <Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} className="bg-blue-50 border-blue-200" data-testid="prod-nombre" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Precio Venta</Label><Input type="number" step="0.01" value={form.precio_venta} onChange={(e) => set("precio_venta", e.target.value)} className="bg-blue-50 border-blue-200 font-mono" data-testid="prod-precio" /></div>
        <div><Label>Costo Unitario</Label><Input type="number" step="0.01" value={form.costo_unitario} onChange={(e) => set("costo_unitario", e.target.value)} className="bg-blue-50 border-blue-200 font-mono" data-testid="prod-costo" /></div>
        <div><Label>Pollos Equivalentes</Label><Input type="number" step="0.01" value={form.pollos_equivalentes} onChange={(e) => set("pollos_equivalentes", e.target.value)} className="bg-blue-50 border-blue-200 font-mono" data-testid="prod-pollos" /></div>
        <div className="flex items-end gap-2">
          <Switch checked={form.activo} onCheckedChange={(v) => set("activo", v)} id="activo" />
          <Label htmlFor="activo">Activo</Label>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={guardar} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="prod-guardar">{busy ? "Guardando…" : "Guardar"}</Button>
      </DialogFooter>
    </div>
  );
}

const Th = ({ children, right }) => <th className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 ${right ? "text-right" : ""}`}>{children}</th>;
const Td = ({ children, right, mono, className = "" }) => <td className={`px-4 py-2.5 ${right ? "text-right" : ""} ${mono ? "font-mono" : ""} ${className}`}>{children}</td>;
